import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  CUSTOMER_SEGMENTS,
  createCustomer,
  fetchCustomers,
  readSession,
  humanizeApiError,
  type CreateCustomerInput,
  type CustomerSegment,
  type CustomerSummary,
} from '../lib/api.js';
import {
  CustomerForm,
  type CustomerCreatePrefill,
} from '../components/customers/CustomerForm.js';
// #684 — the create affordance is gated on `customers:write:all`, the same
// permission the server enforces on POST /api/admin/crm/customers. Same idiom
// as QuickBookModal's #683 create gate and CustomerDetail's #653 add-pet gate:
// `rolesHavePermission` + `effectiveRolesOf` (not the single-role
// `roleHasPermission`) so a multi-role session (#428) is judged on the union.
// This is a UX gate only — the server remains the authority.
import { effectiveRolesOf, rolesHavePermission } from '../lib/rbac-matrix.js';

type CustomersProps = {
  config: ClientConfig;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      customers: CustomerSummary[];
      total: number;
      hasMore: boolean;
    };

/**
 * #679 — classify the Quick Book walk-in query into the matching create-form
 * field. The desk searches with whatever they have in hand ("Jane Doe", a
 * phone number, an email); landing it in the WRONG field (a phone number as
 * a first name) would be a lie the receptionist then has to undo, so:
 *   - contains '@'                    → email
 *   - ≥7 digits once separators drop  → phone
 *   - otherwise                       → name (first token = first name,
 *                                       the rest = last name)
 * Exported for direct pinning; the walk-in flow test exercises it through
 * the real page.
 */
export function createPrefillFromQuery(
  raw: string,
): CustomerCreatePrefill | null {
  const q = raw.trim();
  if (q.length === 0) return null;
  if (q.includes('@')) return { email: q };
  const digits = q.replace(/[\s().+-]/g, '');
  if (digits.length >= 7 && /^\d+$/.test(digits)) return { phone: q };
  const [first, ...rest] = q.split(/\s+/);
  if (!first) return null;
  return rest.length > 0
    ? { firstName: first, lastName: rest.join(' ') }
    : { firstName: first };
}

const PAGE_SIZE = 20;
const SEGMENT_LABEL: Record<CustomerSegment, string> = {
  all: 'All',
  active: 'Active',
  unclaimed: 'Unclaimed',
  vip: 'VIP',
  new: 'New',
  lapsed: 'Lapsed',
};

export function Customers({ config }: CustomersProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [segment, setSegment] = useState<CustomerSegment>('all');
  const [offset, setOffset] = useState(0);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [createOpen, setCreateOpen] = useState(false);
  const [createPrefill, setCreatePrefill] =
    useState<CustomerCreatePrefill | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const apiUrl = config.deployment.apiUrl;

  // #684 — can this session actually create a customer? The route guard only
  // asks for `customers:read`, which the accountant holds; the write is
  // `customers:write:all`, which they do not. Rendering the button anyway
  // sent them through a whole form to a 403.
  const canCreateCustomer = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'customers:write:all',
  );

  // #679 — Quick Book's walk-in handoff: `/customers?create=1&q=<searched
  // text>` opens the create modal with the query prefilled in the matching
  // field, then strips the params (replace, not push) so refresh/back never
  // re-opens a stale modal.
  // #684 — the same gate covers the deep link. The button is not the only
  // door; a hand-typed (or bookmarked) ?create=1 must not open a form whose
  // submit the server refuses. The params are still stripped so the URL
  // doesn't stay armed.
  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    if (!canCreateCustomer) {
      setSearchParams({}, { replace: true });
      return;
    }
    setCreatePrefill(createPrefillFromQuery(searchParams.get('q') ?? ''));
    setCreateOpen(true);
    setSearchParams({}, { replace: true });
  }, [canCreateCustomer, searchParams, setSearchParams]);

  // Stable mode object: a new identity each render would re-run
  // CustomerForm's initial-state effect on every parent re-render while the
  // modal is open (wiping in-progress typing when the list fetch resolves).
  const createMode = useMemo(
    () => ({ kind: 'create' as const, prefill: createPrefill ?? undefined }),
    [createPrefill],
  );

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    try {
      const res = await fetchCustomers(apiUrl, token, {
        search: search || undefined,
        segment,
        limit: PAGE_SIZE,
        offset,
      });
      setState({
        kind: 'ready',
        customers: res.customers,
        total: res.pagination.total,
        hasMore: res.pagination.has_more,
      });
    } catch (err) {
      const message = humanizeApiError(err instanceof Error ? err.message : null);
      setState({ kind: 'error', message });
    }
  }, [apiUrl, offset, search, segment]);

  useEffect(() => {
    void load();
  }, [load]);

  // Debounce-as-you-type — 300ms is short enough to feel instant on phone /
  // email / pet-name searches and avoids a roundtrip per keystroke. Resets
  // pagination so a typed query always starts from page 0.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== search) {
        setOffset(0);
        setSearch(trimmed);
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput, search]);

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setOffset(0);
    setSearch(searchInput.trim());
  };

  const handleSegmentChange = (next: CustomerSegment): void => {
    setOffset(0);
    setSegment(next);
  };

  const handleCreate = async (
    input: CreateCustomerInput,
  ): Promise<CustomerSummary> => {
    const { token } = readSession();
    if (!token) throw new Error('Not authenticated');
    const res = await createCustomer(apiUrl, token, input);
    return res.customer;
  };

  const handleCreated = (customer: CustomerSummary): void => {
    // Navigate straight to detail so the staffer can immediately add a dog,
    // booking, or note without re-finding the row.
    navigate(`/customers/${customer.id}`);
  };

  const pageInfo = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const start = state.customers.length === 0 ? 0 : offset + 1;
    const end = offset + state.customers.length;
    return `${start}–${end} of ${state.total}`;
  }, [state, offset]);

  return (
    <section className="ferro-admin-page" aria-busy={state.kind === 'loading'}>
      <header className="ferro-detail__header">
        <h1 className="ferro-section__title">Customers</h1>
        {canCreateCustomer ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => setCreateOpen(true)}
            data-testid="customers-add"
          >
            + Add customer
          </button>
        ) : null}
      </header>

      <form className="ferro-toolbar" onSubmit={handleSearchSubmit} role="search">
        <label className="ferro-field ferro-toolbar__search">
          <span className="ferro-field__label">Search</span>
          <input
            type="search"
            name="customerSearch"
            className="ferro-field__input"
            placeholder="Name, email, phone, or pet name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="customers-search"
          />
        </label>
        <button type="submit" className="ferro-btn ferro-btn--ghost">
          Search
        </button>
      </form>

      <div className="ferro-segmented" role="tablist" aria-label="Customer segment">
        {CUSTOMER_SEGMENTS.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={segment === s}
            className={`ferro-segmented__btn${
              segment === s ? ' ferro-segmented__btn--active' : ''
            }`}
            onClick={() => handleSegmentChange(s)}
          >
            {SEGMENT_LABEL[s]}
          </button>
        ))}
      </div>

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status">
          Loading customers…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {state.customers.length === 0 ? (
            <p className="ferro-muted">No customers match these filters.</p>
          ) : (
            <ul className="ferro-table" aria-label="Customer list">
              {state.customers.map((c) => {
                // #328 A#4 — dog-first: lead with the pet name(s), with the
                // owner + contact in the meta line. `dogs` is optional on the
                // wire (older server builds, or non-CRM payloads, omit it), so
                // default to [] — never read `.length` on undefined.
                const dogs = c.dogs ?? [];
                return (
                  <li key={c.id} className="ferro-table__row">
                    <Link to={`/customers/${c.id}`} className="ferro-table__link">
                      <div className="ferro-table__primary">
                        {dogs.length > 0 ? `🐕 ${dogs.join(', ')}` : c.name}
                      </div>
                      <div className="ferro-table__meta">
                        {[
                          dogs.length > 0 ? c.name : null,
                          c.email || null,
                          c.phone || null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                      <div className="ferro-table__badge">
                        {c.points_balance} pts
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}

          <nav className="ferro-pagination" aria-label="Pagination">
            <span className="ferro-muted">{pageInfo}</span>
            <div className="ferro-pagination__buttons">
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </button>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                disabled={!state.hasMore}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          </nav>
        </>
      ) : null}

      <CustomerForm
        isOpen={createOpen}
        mode={createMode}
        onClose={() => {
          setCreateOpen(false);
          // A later manual "+ Add customer" starts blank, not with a stale
          // walk-in query.
          setCreatePrefill(null);
        }}
        onSubmitCreate={handleCreate}
        onCreated={handleCreated}
      />
    </section>
  );
}
